import { useRef, useState } from "react"
import { Box, HStack, IconButton } from "@chakra-ui/react"
import { LuArrowUp } from "react-icons/lu"
import { Section, Text } from "@/components"
import { useIrrCompanies } from "@/hooks"
import { CommissionsComponent } from "../commissions"
import { Top20ChartsComponent } from "./components/Top20ChartsComponent"
import { FundCashFlowsComponent } from "./components/FundCashFlowsComponent"

const TABS = [
  { key: "top20", label: "Top 20 Charts" },
  { key: "commissions", label: "Commissions" },
  { key: "cashflows", label: "Distributions & Capital Calls" },
]

const TabBar = ({
  activeTab,
  setActiveTab,
}: {
  activeTab: string
  setActiveTab: (t: string) => void
}) => (
  <HStack gap={0} borderBottomWidth="1px" borderColor="border.default" mb={6} pb={0}>
    {TABS.map((tab) => {
      const isActive = activeTab === tab.key
      return (
        <Box
          key={tab.key}
          px={4}
          py={2.5}
          cursor="pointer"
          borderBottomWidth="2px"
          borderBottomColor={isActive ? "blue.500" : "transparent"}
          onClick={() => setActiveTab(tab.key)}
          mr={1}
        >
          <Text
            fontSize="sm"
            fontWeight={isActive ? "semibold" : "medium"}
            color={isActive ? "blue.500" : "text.secondary"}
          >
            {tab.label}
          </Text>
        </Box>
      )
    })}
  </HStack>
)

export const ReportsComponent = () => {
  const [activeTab, setActiveTab] = useState("top20")
  const sectionRef = useRef<HTMLDivElement>(null)
  const { data: irrCompanyFunds = [], isLoading } = useIrrCompanies()

  const scrollToTop = () => sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })

  // Commissions tab: CommissionsComponent manages its own full-height scroll container.
  // Render the tab bar above it in a flex column so they share the viewport without nesting scrollers.
  if (activeTab === "commissions") {
    return (
      <Box h="100vh" display="flex" flexDirection="column" overflow="hidden" bg="page.bg">
        <Box px={10} pt={5} flexShrink={0} bg="page.bg">
          <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
        </Box>
        <Box flex="1" overflow="hidden">
          <CommissionsComponent />
        </Box>
      </Box>
    )
  }

  // Distributions & Capital Calls tab: scrollable Section
  if (activeTab === "cashflows") {
    return (
      <Section ref={sectionRef} overflowY="auto" h="100vh">
        <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
        <FundCashFlowsComponent />
      </Section>
    )
  }

  // Top 20 Charts tab: own scrollable Section
  return (
    <Section ref={sectionRef} overflowY="auto" h="100vh">
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
      <Top20ChartsComponent irrCompanyFunds={irrCompanyFunds} isLoading={isLoading} />
      <IconButton
        aria-label="Scroll to top"
        size="lg"
        borderRadius={10}
        boxShadow="lg"
        bg="fab.bg"
        onClick={scrollToTop}
        position="fixed"
        bottom={6}
        right={6}
      >
        <LuArrowUp />
      </IconButton>
    </Section>
  )
}
